import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, Mail } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | success | expired | invalid

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    axios.post(`${API}/auth/verify-email`, { token }, { withCredentials: true })
      .then(res => {
        if (res.data.user) {
          login(res.data.user, res.data.token);
        }
        setStatus("success");
      })
      .catch(err => {
        const detail = err.response?.data?.detail;
        setStatus(detail === "token_expired" ? "expired" : "invalid");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-sm">

        {/* Loading */}
        {status === "loading" && (
          <div data-testid="verify-loading">
            <Loader2 size={40} className="animate-spin text-blue-600 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-800">Verifying your email...</h2>
            <p className="text-sm text-slate-500 mt-2">This will only take a moment.</p>
          </div>
        )}

        {/* Success */}
        {status === "success" && (
          <div data-testid="verify-success-panel">
            <div className="w-16 h-16 bg-emerald-50 border border-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={28} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Email verified</h2>
            <p className="text-sm text-slate-600 mb-8">
              Your account is active. You're being signed in automatically.
            </p>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate("/dashboard")}
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* Expired */}
        {status === "expired" && (
          <div data-testid="verify-expired-panel">
            <div className="w-16 h-16 bg-amber-50 border border-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Mail size={28} className="text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Link expired</h2>
            <p className="text-sm text-slate-600 mb-8">
              This verification link has expired (links are valid for 24 hours). Request a new one by signing in.
            </p>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate("/auth")}
              data-testid="back-to-auth-btn"
            >
              Back to Sign In
            </Button>
          </div>
        )}

        {/* Invalid */}
        {status === "invalid" && (
          <div data-testid="verify-invalid-panel">
            <div className="w-16 h-16 bg-red-50 border border-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <XCircle size={28} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid link</h2>
            <p className="text-sm text-slate-600 mb-8">
              This verification link is invalid or has already been used. Please sign in to request a new link.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate("/auth")}
              data-testid="back-to-auth-btn"
            >
              Back to Sign In
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
